import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Clock,
  Target,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Play,
  BarChart3,
  TrendingUp,
  Calendar,
  Award,
  RotateCcw,
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Star,
  CheckCircle,
  XCircle,
  Save,
  Eye,
  Bookmark,
  RefreshCw
} from 'lucide-react';

import { QuestionComponent } from '../../components/QuestionTypes';
import { PracticeSetCard } from '../../components/PracticeSetCard';

import { useUserData } from '../../contexts/UserDataContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { savePracticeSession, saveWrongQuestion, saveUserRevisionData, getUserRevisionDataByUserId } from '../../firebase/collections';

const PracticeSets = () => {
  const navigate = useNavigate();
  const { userData: userDataFromContext } = useUserData();
  const { currentUser } = useRealtime();
  const [practiceSets, setPracticeSets] = useState([]);
  const [currentSet, setCurrentSet] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [performance, setPerformance] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const topics = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'History'];
  const difficulties = ['Easy', 'Medium', 'Hard'];

  // Load practice sets from Firebase
  useEffect(() => {
    const loadPracticeSets = async () => {
      try {
        // Load practice sets from Firebase
        // For now, set empty array
        setPracticeSets([]);
      } catch (error) {
        setPracticeSets([]);
      }
    };
    
    loadPracticeSets();
  }, []);

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const calculateScore = (currentSet, answers) => {
    let totalScore = 0;
    let maxScore = 0;
    
    currentSet.questions.forEach(question => {
      const answer = answers[question.id];
      if (answer !== undefined) {
        if (question.type === 'multiple-choice' && answer === question.correctAnswer) {
          totalScore += question.markingScheme.correct;
        } else if (question.type === 'integer' && answer === question.correctAnswer) {
          totalScore += question.markingScheme.correct;
        } else {
          totalScore += question.markingScheme.incorrect;
        }
      }
      maxScore += question.markingScheme.correct;
    });
    
    return Math.max(0, Math.round((totalScore / maxScore) * 100));
  };

  const handleSubmitPractice = async () => {
    if (!currentUser?.uid) {
      return;
    }

    setIsSaving(true);
    try {
      // Calculate results
      const correctAnswers = currentSet.questions.filter((q, index) => 
        answers[q.id] === q.correctAnswer
      ).length;
      const wrongAnswers = currentSet.questions.filter((q, index) => 
        answers[q.id] !== undefined && answers[q.id] !== q.correctAnswer
      ).length;
      const unanswered = currentSet.questions.filter(q => 
        answers[q.id] === undefined || answers[q.id] === null
      ).length;
      
      const score = Math.round((correctAnswers / currentSet.questions.length) * 100);
      const passed = score >= 60;
      
      // Create detailed performance record
      const performanceRecord = {
        setId: currentSet.id,
        setTitle: currentSet.title,
        topic: currentSet.topic,
        difficulty: currentSet.difficulty,
        date: new Date().toISOString(),
        score: score,
        passed: passed,
        totalQuestions: currentSet.questions.length,
        correctAnswers: correctAnswers,
        wrongAnswers: wrongAnswers,
        unanswered: unanswered,
        accuracy: Math.round((correctAnswers / currentSet.questions.length) * 100),
        timeSpent: 0, // Could track actual time spent
        answers: answers,
        questions: currentSet.questions.map(q => ({
          id: q.id,
          question: q.question,
          type: q.type,
          options: q.options,
          correctAnswer: q.correctAnswer,
          Answer: answers[q.id],
          isCorrect: answers[q.id] === q.correctAnswer,
          topic: q.topic || currentSet.topic,
          difficulty: q.difficulty || currentSet.difficulty
        })),
        wrongQuestions: currentSet.questions.filter(q => 
          answers[q.id] !== undefined && answers[q.id] !== q.correctAnswer
        ).map(q => ({
          id: q.id,
          question: q.question,
          type: q.type || 'multiple-choice',
          options: q.options || ['Option A', 'Option B', 'Option C', 'Option D'],
          correctAnswer: q.correctAnswer || 'Answer not available',
          userAnswer: answers[q.id],
          topic: q.topic || currentSet.topic,
          subject: q.subject || currentSet.subject || 'General',
          course: q.course || currentSet.course || 'General Course',
          chapter: q.chapter || q.section || 'General Chapter',
          difficulty: q.difficulty || currentSet.difficulty,
          explanation: q.explanation || `The correct answer is ${q.options ? q.options[q.correctAnswer] : q.correctAnswer}`
        }))
      };
      
      // Save practice session to Firebase
      const sessionResult = await savePracticeSession(userDataFromContext.uid, {
        setId: currentSet.id,
        setTitle: currentSet.title,
        topic: currentSet.topic,
        difficulty: currentSet.difficulty,
        score: score,
        passed: passed,
        totalQuestions: currentSet.questions.length,
        correctAnswers: correctAnswers,
        wrongAnswers: wrongAnswers,
        accuracy: performanceRecord.accuracy,
        timeSpent: performanceRecord.timeSpent,
        answers: answers
      });
      
      // Save wrong questions to Firebase
      for (const wrongQ of performanceRecord.wrongQuestions) {
        await saveWrongQuestion(userDataFromContext.uid, {
          questionId: wrongQ.id,
          question: wrongQ.question,
          type: wrongQ.type,
          options: wrongQ.options,
          correctAnswer: wrongQ.correctAnswer,
          userAnswer: wrongQ.userAnswer,
          topic: wrongQ.topic,
          subject: wrongQ.subject,
          course: wrongQ.course,
          chapter: wrongQ.chapter,
          difficulty: wrongQ.difficulty,
          explanation: wrongQ.explanation,
          practiceSet: performanceRecord.setTitle,
          practiceDate: performanceRecord.date
        });
      }
      
      // Get existing revision data
              const existingRevisionResult = await getUserRevisionDataByUserId(userDataFromContext.uid);
      const existingRevisionData = existingRevisionResult.success ? (existingRevisionResult.data || {}) : {};
      
      // Update topic performance
      const topicPerformance = existingRevisionData.topicPerformance || {};
      if (!topicPerformance[performanceRecord.topic]) {
        topicPerformance[performanceRecord.topic] = {
          totalQuestions: 0,
          correctAnswers: 0,
          wrongAnswers: 0,
          practiceSessions: 0,
          averageAccuracy: 0
        };
      }
      
      topicPerformance[performanceRecord.topic].totalQuestions += performanceRecord.totalQuestions;
      topicPerformance[performanceRecord.topic].correctAnswers += performanceRecord.correctAnswers;
      topicPerformance[performanceRecord.topic].wrongAnswers += performanceRecord.wrongAnswers;
      topicPerformance[performanceRecord.topic].practiceSessions += 1;
      topicPerformance[performanceRecord.topic].averageAccuracy = Math.round(
        (topicPerformance[performanceRecord.topic].correctAnswers / 
         topicPerformance[performanceRecord.topic].totalQuestions) * 100
      );
      
      // Save updated revision data to Firebase
              const revisionResult = await saveUserRevisionData(userDataFromContext.uid, {
        topicPerformance: topicPerformance,
        lastPracticeSession: performanceRecord.date,
        totalPracticeSessions: (existingRevisionData.totalPracticeSessions || 0) + 1,
        totalWrongQuestions: (existingRevisionData.totalWrongQuestions || 0) + wrongAnswers,
        lastUpdated: new Date().toISOString()
      });
      
      // Update local performance state
      setPerformance(prev => ({
        ...prev,
        [currentSet.id]: {
          attempts: (prev[currentSet.id]?.attempts || 0) + 1,
          lastScore: score,
          passed: passed,
          averageScore: prev[currentSet.id] 
            ? Math.round((prev[currentSet.id].averageScore * prev[currentSet.id].attempts + score) / (prev[currentSet.id].attempts + 1))
            : score,
          lastAttempt: new Date().toISOString(),
          totalQuestions: performanceRecord.totalQuestions,
          correctAnswers: performanceRecord.correctAnswers,
          wrongAnswers: performanceRecord.wrongAnswers
        }
      }));
      
      // Show results
      setShowResults(true);
      
      // Show success message
      
         } catch (error) {
     } finally {
       setIsSaving(false);
     }
   };

   const resetPractice = () => {
     setCurrentQuestionIndex(0);
     setAnswers({});
     setShowResults(false);
   };

  const getFilteredSets = () => {
    let filtered = practiceSets;
    
    if (selectedTopic !== 'all') {
      filtered = filtered.filter(set => set.topic === selectedTopic);
    }
    
    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(set => set.difficulty === selectedDifficulty);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(set => 
        set.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        set.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

     const filteredSets = getFilteredSets();
   const currentQuestion = currentSet?.questions[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Practice Sets
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Master topics with targeted practice and adaptive learning
          </p>
        </div>

        {/* Practice Session */}
        {currentSet && !showResults ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold">{currentSet.title}</h2>
                  <p className="text-blue-100">{currentSet.description}</p>
                </div>
                <div className="text-right">
                                     <p className="text-sm text-blue-100">
                     Question {currentQuestionIndex + 1} of {currentSet.questions.length}
                   </p>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-blue-700 rounded-full h-2">
                                 <div 
                   className="bg-green-400 h-2 rounded-full transition-all duration-300"
                   style={{ width: `${((currentQuestionIndex + 1) / currentSet.questions.length) * 100}%` }}
                 ></div>
              </div>
              
              {/* Question Navigation */}
              <div className="flex flex-wrap gap-2 mt-4">
                {currentSet.questions.map((_, index) => (
                                     <button
                     key={index}
                     onClick={() => setCurrentQuestionIndex(index)}
                     className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                       index === currentQuestionIndex ? 'bg-white text-blue-600'
                         : answers[currentSet.questions[index].id] !== undefined
                         ? 'bg-green-400 text-white'
                         : 'bg-blue-700 text-blue-200 hover:bg-blue-600'
                     }`}
                   >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {currentQuestion && (
                <div>
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm">
                        {currentQuestion.topic || currentSet.topic}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full text-sm">
                        {currentQuestion.difficulty || currentSet.difficulty}
                      </span>
                    </div>
                    
                                         <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                       {currentQuestion.question}
                     </h3>
                     
                     <div className="text-sm text-gray-600 mb-4">
                       <strong>Marking Scheme:</strong> Correct: +{currentQuestion.markingScheme?.correct || 4}, 
                       Incorrect: {currentQuestion.markingScheme?.incorrect || -1}
                     </div>
                   </div>

                   <QuestionComponent
                     question={currentQuestion}
                     selectedAnswer={answers[currentQuestion.id]}
                     onAnswerSelect={handleAnswerSelect}
                   />
                </div>
              )}

              <div className="flex justify-between mt-8">
                                 <button
                   onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                   disabled={currentQuestionIndex === 0}
                   className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50"
                 >
                   Previous
                 </button>

                 {currentQuestionIndex === currentSet.questions.length - 1 ? (
                  <button
                    onClick={handleSubmitPractice}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Submit Practice
                  </button>
                                 ) : (
                   <button
                     onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                     className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                   >
                     Next
                   </button>
                 )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Results */}
        {showResults && currentSet && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-8"
          >
            {/* Results Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
              <div className="text-center">
                {performance[currentSet.id]?.passed ? (
                  <CheckCircle className="h-16 w-16 text-green-300 mx-auto mb-4" />
                ) : (
                  <XCircle className="h-16 w-16 text-red-300 mx-auto mb-4" />
                )}
                <h2 className="text-2xl font-bold mb-2">
                  {performance[currentSet.id]?.passed ? 'Practice Completed!' : 'Practice Failed'}
                </h2>
                <p className="text-blue-100">
                  Your score: {performance[currentSet.id]?.lastScore}% 
                  (Passing score: {currentSet.passingScore}%)
                </p>
              </div>
            </div>

            {/* Detailed Results */}
            <div className="p-6">
              {/* Score Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-green-600 mb-1">
                    {currentSet.questions.filter((q, index) => {
                      const answer = answers[q.id];
                      return answer === q.correctAnswer;
                    }).length}
                  </div>
                  <div className="text-sm text-green-700 dark:text-green-300">Correct Answers</div>
                </div>
                
                <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-red-600 mb-1">
                    {currentSet.questions.filter((q, index) => {
                      const answer = answers[q.id];
                      return answer !== undefined && answer !== q.correctAnswer;
                    }).length}
                  </div>
                  <div className="text-sm text-red-700 dark:text-red-300">Wrong Answers</div>
                </div>
                
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    {currentSet.questions.filter((q, index) => {
                      const answer = answers[q.id];
                      return answer === undefined || answer === null;
                    }).length}
                  </div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">Unanswered</div>
                </div>
              </div>

              {/* Question Review */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Question Review
                </h3>
                <div className="space-y-4">
                                     {currentSet.questions.map((question, index) => {
                     const userAnswer = answers[question.id];
                     const isCorrect = userAnswer === question.correctAnswer;
                     const isAnswered = userAnswer !== undefined && userAnswer !== null;
                    
                    return (
                      <div 
                        key={question.id}
                        className={`p-4 border rounded-lg ${
                          isCorrect 
                            ? 'border-green-200 bg-green-50 dark:bg-green-900/20' 
                            : isAnswered 
                            ? 'border-red-200 bg-red-50 dark:bg-red-900/20'
                            : 'border-gray-200 bg-gray-50 dark:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                              Question {index + 1}
                            </span>
                            {isCorrect ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : isAnswered ? (
                              <XCircle className="h-4 w-4 text-red-600" />
                            ) : (
                              <div className="h-4 w-4 border-2 border-gray-300 rounded-full"></div>
                            )}
                          </div>
                          <span className={`text-sm font-medium ${
                            isCorrect ? 'text-green-600' : isAnswered ? 'text-red-600' : 'text-gray-500'
                          }`}>
                            {isCorrect ? 'Correct' : isAnswered ? 'Incorrect' : 'Unanswered'}
                          </span>
                        </div>
                        
                        <div className="mb-3">
                          <p className="text-gray-900 dark:text-white">{question.question}</p>
                        </div>
                        
                        {question.options && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Options:</p>
                            <div className="space-y-1">
                              {question.options.map((option, optIndex) => (
                                <div 
                                  key={optIndex}
                                  className={`p-2 rounded ${
                                    optIndex === question.correctAnswer
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                                                                             : optIndex === userAnswer && !isCorrect
                                      ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                  }`}
                                >
                                  <span className="font-medium">{optIndex + 1}.</span> {option}
                                  {optIndex === question.correctAnswer && (
                                    <span className="ml-2 text-green-600 dark:text-green-400 flex items-center gap-1">
                                      <CheckCircle className="w-4 h-4" /> Correct
                                    </span>
                                  )}
                                                                     {optIndex === userAnswer && !isCorrect && (
                                    <span className="ml-2 text-red-600 dark:text-red-400 flex items-center gap-1">
                                      <XCircle className="w-4 h-4" /> Your Answer
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {!isCorrect && (
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                              Correct Answer:
                            </p>
                            <p className="text-blue-900 dark:text-blue-100">
                              {question.options 
                                ? question.options[question.correctAnswer] 
                                : question.correctAnswer}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                                 <button
                   onClick={() => {
                     setShowResults(false);
                     setCurrentQuestionIndex(0);
                     setAnswers({});
                   }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Retry Practice
                </button>
                <button
                  onClick={() => {
                    setCurrentSet(null);
                    setShowResults(false);
                  }}
                  className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Back to Sets
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Practice Sets List */}
        {!currentSet && (
          <>
            {/* Controls */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="all">All Topics</option>
                    {topics.map(topic => (
                      <option key={topic} value={topic}>{topic}</option>
                    ))}
                  </select>

                  <select
                    value={selectedDifficulty}
                    onChange={(e) => setSelectedDifficulty(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="all">All Difficulties</option>
                    {difficulties.map(difficulty => (
                      <option key={difficulty} value={difficulty}>{difficulty}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => setShowCreateForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Sets
                </button>
              </div>

              <div className="mt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search practice sets..."
                    value={searchTerm}
                                         onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

                        {/* Practice Sets Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSets.map((set, index) => (
                <PracticeSetCard
                  key={set.id}
                  set={set}
                  performance={performance}
                  onStartPractice={setCurrentSet}
                  index={index}
                />
              ))}
            </div>

            {filteredSets.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No practice sets found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Create your first practice set to start learning
                </p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Practice Set
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PracticeSets;
